import { TProductType } from 'typings';

// Purchases list filter: send every `subscribe_status` except `Expired`, so the
// billing + payment-method screens drop fully-expired subscriptions while still
// showing Active / Cancelled / RenewalFailed (RenewalFailed covers the failed +
// grace-period rows the retry / re-subscribe flow acts on). Spec enum (Confluence
// "API implementations for Invoice and Payment method page"): Active, Cancelled,
// Expired, RenewalFailed — comma-separated.
const PURCHASES_SUBSCRIBE_STATUS = 'Active,Cancelled,Expired,RenewalFailed';

export const PIGGYBANK_ENDPOINTS = {
  // Plan sku apis
  HOME_PLANS: (country: string, category: string, type: string) =>
    `piggybank/products/skus?country=${country}&product_ids=HomeSecurity&sku_categories=${category}&price_types=${type}`,
  // Data SKUs are space-scoped and not tied to a product, so they are queried by
  // scope + sku_category instead of product_ids.
  DATA_PLANS: (country: string, type: string) =>
    `piggybank/products/skus?country=${country}&scope=Space&sku_categories=Data&price_types=${type}`,
  BIRD_PLAN_LIST: (country: string) =>
    `piggybank/products/skus?country=${country}&product_ids=Bird`,
  PET_PLAN_LIST: (country: string) =>
    `piggybank/products/skus?country=${country}&product_ids=Pet`,
  BABY_PLAN_LIST: (country: string) =>
    `piggybank/products/skus?country=${country}&product_ids=Baby`,

  TRAIL_CAM_PLAN_LIST: ({
    country,
    sku_category
  }: {
    country: string;
    sku_category: string;
  }) =>
    `piggybank/products/skus?country=${country}&product_ids=Trail&sku_categories=${sku_category}`,
  GET_CURRENT_PLAN_SKU: (
    skuId: string,
    country: string,
    price_type: string
  ) => {
    return `piggybank/skus/${skuId}?country=${country}&price_type=${price_type}`;
  },
  //  trial skip apis
  SKIP_PLAN_REASON_LIST: (
    productCategory: string,
    subscriptionType: string
  ) => {
    return `piggybank/purchases/trial/skip-reason-list?product_type=${productCategory}&subscription_type=${subscriptionType}`;
  },
  USER_SKIP_REASON: 'piggybank/purchases/trial/user-skip-reason',
  // Payment methods api
  // PM stands for Payment Method
  PAYMENT_METHODS: 'piggybank/payment_gateway/payment_methods',
  SETUP_INTENT: 'piggybank/payment_gateway/setup_intents',
  CHANGE_PM_IN_SUBSCRIPTION: (subscriptionId: string) => {
    return `piggybank/subscriptions/${subscriptionId}/payment-method`;
  },
  DELETE_PM: (paymentMethodId: string) => {
    return `piggybank/payment_gateway/payment_methods/${paymentMethodId}`;
  },
  UPDATE_DEFAULT_PM: (paymentMethodId: string) => {
    return `piggybank/payment_gateway/payment_methods/${paymentMethodId}/default`;
  },

  // subscription Apis
  BUY_SUBSCRIPTION: '/piggybank/subscriptions',
  PRORATE_CHARGE: (purchaseId: string) => {
    return `piggybank/subscriptions/${purchaseId}/prorate_charges`;
  },
  UPGRADE_SUBSCRIPTION: (subscriptionId: string) =>
    `piggybank/subscriptions/${subscriptionId}/upgrade`,
  DOWNGRADE_SUBSCRIPTION: (subscriptionId: string) =>
    `piggybank/subscriptions/${subscriptionId}/downgrade`,
  DOWNGRADE_SUBSCRIPTION_V2: (subscriptionId: string) =>
    `piggybank/subscriptions/${subscriptionId}/upgrade`,
  CREATE_PURCHASE_ID: 'piggybank/purchases',
  CHECKOUT_SESSION: (subscriptionId: string) => {
    return `piggybank/subscriptions/${subscriptionId}/checkout/session`;
  },
  CANCEL_SUBSCRIPTION: (subscriptionId: string) => {
    return `piggybank/subscriptions/${subscriptionId}`;
  },
  REACTIVATE_SUBSCRIPTION: (subscriptionId: string) => {
    return `piggybank/subscriptions/${subscriptionId}/reactivate`;
  },

  // Multiple Plan Subscriptions (FCM-2916). See Confluence "Multi-Quantity
  // Subscriptions — API Guide for UI". Both take { quantity } in the body.
  QUANTITY_PREVIEW: (subscriptionId: string) =>
    `piggybank/subscriptions/${subscriptionId}/quantity/preview`,
  CHANGE_QUANTITY: (subscriptionId: string) =>
    `piggybank/subscriptions/${subscriptionId}/quantity`,

  // Retry payment CTA (FCM-2514). Takes the subscriptionId passed by the app on
  // /retryPayment and a payment_method_id in the body. 204 = success.
  // See Confluence "Payment Recovery page (User portal)".
  RETRY_PAYMENT: (subscriptionId: string) => {
    return `piggybank/subscriptions/${subscriptionId}/retry-payment`;
  },

  // trial apis
  CHECK_TRIAL: (productType: TProductType) => {
    return `piggybank/purchases/trial?product_id=${productType}`;
  },
  // Data free trial is per space, not per product.
  CHECK_DATA_TRIAL: 'piggybank/purchases/trial?scope=Space',

  // invoice apis
  GET_TRANSACTION: `/piggybank/purchases/transactions`,
  // Download Invoice (Confluence: Invoice and Payment method page):
  // GET .../transactions/{transaction_id}/invoice → { invoice_link }.
  DOWNLOAD_INVOICES: (transaction_id: string) => {
    return `piggybank/purchases/transactions/${transaction_id}/invoice`;
  },

  // My Orders + Payment Methods — purchases list + per-subscription invoices.
  // Sends `subscribe_status` to exclude `Expired` (see PURCHASES_SUBSCRIBE_STATUS).
  PURCHASES_LIST: (
    spaceId: string,
    subscribeStatus: string = PURCHASES_SUBSCRIBE_STATUS
  ) =>
    `/piggybank/purchases?space_id=${encodeURIComponent(
      spaceId
    )}&subscribe_status=${subscribeStatus}`,
  // Failed Payments (Confluence: Invoice and Payment method page). Authoritative
  // count behind the failed-payment banner → { failed_count }. Backend-computed
  // so it reflects resolved failures after a retry / re-subscribe + refetch.
  FAILED_COUNT: '/piggybank/purchases/failed-count',
  // List Invoices (Confluence: Invoice and Payment method page).
  // Spec query param is `purchase_id` (one subscription's invoices). Spec leaves
  // `limit` unspecified but supports pagination (skip / next_page / total_count);
  // default to 50 to cover the common case without a "load more" round-trip.
  TRANSACTIONS_BY_PURCHASE: (purchaseId: string, limit = 50, skip = 0) =>
    `/piggybank/purchases/transactions?purchase_id=${encodeURIComponent(
      purchaseId
    )}&limit=${limit}&skip=${skip}`,

  // To do : Replace below urls with actual urls of bird skip reason
  BIRD_TRIAL_SKIP_REASON: `piggybank/purchases/trial/skip-reason-list`,
  BIRD_USER_SKIP_REASON: 'piggybank/purchases/trial/user-skip-reason',

  PET_TRIAL_SKIP_REASON: `piggybank/purchases/trial/skip-reason-list`,
  PET_USER_SKIP_REASON: 'piggybank/purchases/trial/user-skip-reason',

  TRAIL_CAM_TRIAL_SKIP_REASON: `piggybank/purchases/trial/skip-reason-list`,
  TRAIL_CAM_USER_SKIP_REASON: 'piggybank/purchases/trial/user-skip-reason',

  BABY_TRIAL_SKIP_REASON: `piggybank/purchases/trial/skip-reason-list`,
  BABY_USER_SKIP_REASON: 'piggybank/purchases/trial/user-skip-reason'
};

export const ATLAS_ENDPOINTS = {
  // user apis
  USER: 'atlas/users',

  // user space apis
  SPACE: 'atlas/spaces',

  // account delete apis
  REQUEST_ACCOUNT_DELETE: 'atlas/users/initiate-user-delete',

  // session apis
  CREATE_SESSION: 'atlas/users/session',
  LOGOUT_SESSION: 'atlas/users/session/logout',

  // one time token apis
  GENERATE_ONE_TIME_TOKEN: 'atlas/users/one-time-token',
  VERIFY_ONE_TIME_TOKEN: 'atlas/users/one-time-token/login',

  // password reset apis — unauthenticated; the emailed token is the only capability
  CONFIRM_PASSWORD_RESET: 'atlas/users/password-reset/confirm',

  // devices apis
  AUTHENTICATE_DEVICE: (spaceId: string, deviceId: string) => {
    return `atlas/spaces/${spaceId}/devices/${deviceId}`;
  },
  DEVICES_IN_SPACE: (spaceId: string) => {
    return `atlas/spaces/${spaceId}/devices`;
  },
  DELETED_DEVICES_IN_SPACE: (spaceId: string) => {
    return `atlas/spaces/${spaceId}/devices/deleted`;
  },

  // Records/reads whether a space has already seen the post-pairing
  // promotion for a given product type (FCM-2832 follow-up) — single source
  // of truth for first-time vs returning pairing, replacing devices/deleted.
  // `productType` scopes the GET so a paid HomeSecurity user still sees the
  // first-time promo for a different product type (e.g. Bird) paired into the
  // same space. Not needed for POST — the body already carries product_type.
  PROMOTION_SKIPS_IN_SPACE: (spaceId: string, productType?: TProductType) => {
    return productType
      ? `atlas/spaces/${spaceId}/promotion-skips?product_type=${productType}`
      : `atlas/spaces/${spaceId}/promotion-skips`;
  },

  // market place apis
  GET_ALL_MARKET_PLACE: (spaceId: string, deviceId: string) => {
    return `atlas/spaces/${spaceId}/devices/${deviceId}/marketplace`;
  },
  CAMERA_PURCHASE_PLATFORM: (spaceId: string, deviceId: string) => {
    return `atlas/spaces/${spaceId}/devices/${deviceId}/marketplace`;
  },

  // subscription apis
  GET_SUBSCRIPTION: 'atlas/subscriptions',
  ADD_DEVICE_IN_SUBSCRIPTION: (subscriptionId: string) => {
    return `atlas/subscriptions/${subscriptionId}/devices`;
  },
  // Single subscription by id — the subscriptions endpoint filtered by `_id`.
  // Returns one subscription object (card details, amount, dates, status); the
  // same data the mobile apps use for the failed-payment banner. FCM-2514 passes
  // the /retryPayment subscriptionId here.
  SUBSCRIPTION_BY_ID: (subscriptionId: string) => {
    return `atlas/subscriptions?_id=${encodeURIComponent(subscriptionId)}`;
  },

  // one time data pass details
  GET_ONE_TIME_DATA_PASS_DETAILS: (spaceId: string, purchaseType: string) => {
    return `atlas/spaces/${spaceId}/data?purchase_type=${purchaseType}`;
  },

  // Remaining data across the space's pool -> { total_data_mb, remaining_data_mb }.
  // Used to decide whether enabling auto recharge must charge the user right away.
  GET_POOL_DATA: (spaceId: string) => {
    return `atlas/spaces/${spaceId}/pool-data`;
  },

  // get auto charge details
  GET_AUTO_CHARGE_DETAILS: (spaceId: string) => {
    return `atlas/spaces/${spaceId}/auto-top-up`;
  },

  // update auto charge details
  UPDATE_AUTO_CHARGE_DETAILS: (spaceId: string, autoTopUpId: string) => {
    return `atlas/spaces/${spaceId}/auto-top-up/${autoTopUpId}`;
  },
  // create auto charge details
  CREATE_AUTO_CHARGE_DETAILS: (spaceId: string) => {
    return `atlas/spaces/${spaceId}/auto-top-up`;
  }
};
